import type { WritableDeep } from 'type-fest';

import type { PrintStyleId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { Getters } from '../../../components/DocumentEditor/types/props.type';
import type { DocumentEditorCmp_RecursiveSection } from '../../../components/DocumentEditor/fragments/recursive-section.fragment';

import { MissionQuotationPage_QuotationVersions_QueryDocument } from '../queries/quotation-versions.query';
import type { MissionQuotationWithItsInfo } from '../types/mission-quotation-with-its-info.type';

export const useGetters: () => Getters<MissionQuotationWithItsInfo> = () => {
  const { fileDirectoryCategoriesCodeMap, quotationFileCategoriesCodeMap } =
    useReferencesContext();

  return {
    editable: ((quotation: Readonly<MissionQuotationWithItsInfo>) => ({
      virtualRoot: quotation.virtualRoot,
      code: quotation.code,
      title: quotation.title,
    })) as <W extends boolean = false>(
      editable: W extends false
        ? MissionQuotationWithItsInfo
        : WritableDeep<MissionQuotationWithItsInfo>,
    ) => Readonly<{
      virtualRoot: W extends false
        ? DocumentEditorCmp_RecursiveSection
        : WritableDeep<DocumentEditorCmp_RecursiveSection>;
      title: string;
      code: string;
    }>,
    printing: (quotation: Readonly<MissionQuotationWithItsInfo>) => {
      const {
        printing: {
          printSettings: { addFooters, addHeaders, printStyleId },
          pdfPrints,
          pdfPrintsIncrement,
        },
      } = quotation;

      const printSettings = {
        addFooters,
        addHeaders,
        printStyleId: printStyleId as PrintStyleId,
      };

      return {
        __typename: 'QuotationPrinting' as const,
        printSettings: {
          __typename: 'PrintSettings' as const,
          ...printSettings,
        },
        pdfPrints,
        pdfPrintsIncrement,
      };
    },
    storage: (quotation: Readonly<MissionQuotationWithItsInfo>) => ({
      directoryCategoryId: fileDirectoryCategoriesCodeMap.Quotation._id,
      entityId: quotation._id,
      imageCategory: quotationFileCategoriesCodeMap.TextEditorImages,
      pdfCategory: quotationFileCategoriesCodeMap.PDFPrints,
    }),
    versions: () => ({
      queryDocument: MissionQuotationPage_QuotationVersions_QueryDocument,
    }),
  };
};
